import React, {
  memo, useCallback, useContext, useEffect, useId, useRef, useState,
} from 'react'

import {
  Button, Chat, FavoriteFilled, FavoriteOutlined, Icon, Spinner, classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string } from 'prop-types'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import CommentThread from 'Pods/ThreeDStudio/Shared/Comments/Components/CommentThread/CommentThread'
import Composer from 'Pods/ThreeDStudio/Shared/Comments/Components/Composer/Composer'
import useModelComments from 'Pods/ThreeDStudio/Shared/Comments/Hooks/useModelComments'
import useModelLike from 'Pods/ThreeDStudio/Shared/Likes/Hooks/useModelLike'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import EmptyState from './Components/EmptyState/EmptyState'
import useHasScrollbar from './Hooks/useHasScrollbar'

const chatIcon = <Icon svgComponent={Chat} size={iconSize} notSpaced />
const likedIcon = <Icon svgComponent={FavoriteFilled} size={iconSize} notSpaced />
const likeIcon = <Icon svgComponent={FavoriteOutlined} size={iconSize} notSpaced />

// Right-hand column of the lightbox: the image title, the comments and like chips and the
// thread. The comments chip only shows or hides the composer and the thread; its count
// follows every add and delete. The like chip toggles the viewer's like. No login
// prompt: the library that opens this is only shown to signed-in users. Both counts start
// at 0 and take the value the comments and like requests answer with: the library row's
// counts go stale once the lightbox adds or deletes, so they are not used as a seed.
function CommentsPanel({ slug, title = '' }) {
  const t = useTranslation()
  const { locale, userLoggedIn } = useContext(ThreeDStudioPageContext)
  const pageConfig = usePageConfig()
  const [isExpanded, setIsExpanded] = useState(true)
  const panelRef = useRef(null)
  const bodyRef = useRef(null)
  const sentinelRef = useRef(null)
  const bodyId = useId()

  const {
    comments,
    totalCommentsCount,
    hasMore,
    loadMore,
    isLoading,
    loadFailed,
    submitFailed,
    limitReached,
    isSubmitting,
    addComment,
    addReply,
    deleteComment,
  } = useModelComments({ slug, isOpen: true, syncOpenedModel: false })

  const {
    isLiked, likeCount, isToggling, toggleLike,
  } = useModelLike({ slug, dailyLimitMessage: t('like.dailyLimitReached') })

  const commentsCount = totalCommentsCount ?? 0

  const currentUserId = pageConfig?.currentUserId ?? null
  // A scrollbar that takes up width stands in for the region's right padding, which is
  // dropped while it shows so the comments keep that room.
  const hasScrollbar = useHasScrollbar(bodyRef, `${isExpanded}-${comments.length === 0}`)
  const canComment = !!userLoggedIn

  // Infinite scroll over the paginated root comments, as in CommentsModal. The panel is
  // the scroll container from 960px up; below that it grows with its content, so the
  // sentinel stays in view and the pages load one after another.
  useEffect(() => {
    const sentinel = sentinelRef.current

    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore()
        }
      },
      { root: panelRef.current, rootMargin: '200px', threshold: 0 },
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [hasMore, loadMore, isExpanded])

  const handleToggle = useCallback(() => {
    setIsExpanded(wasExpanded => !wasExpanded)
  }, [])

  // The Delete button unmounts with its comment and would drop focus to <body>, outside
  // the dialog, where Escape no longer closes it. Deferred a frame, as CommentsModal
  // does, so the confirm modal's own focus restore runs first.
  const handleDelete = useCallback((commentId) => {
    deleteComment(commentId)
    requestAnimationFrame(() => panelRef.current?.focus())
  }, [deleteComment])

  return (
    <aside
      ref={panelRef}
      className="image-assistant-lightbox__comments"
      aria-label={t('commentsModal.ariaLabel')}
      tabIndex={-1}
    >
      {title && <h2 className="image-assistant-lightbox__comments-title cgt-body-base-semibold">{title}</h2>}
      <div className="image-assistant-lightbox__comments-chips">
        <Button
          type="secondary"
          size="small"
          variant="with-icon"
          actionType="button"
          icon={chatIcon}
          customClass="image-assistant-lightbox__comments-toggle"
          aria-label={t('commentsModal.commentsCount', { count: commentsCount })}
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          onClick={handleToggle}
        >
          {commentsCount}
        </Button>
        <Button
          type="secondary"
          size="small"
          variant="with-icon"
          actionType="button"
          icon={isLiked ? likedIcon : likeIcon}
          customClass="image-assistant-lightbox__like-toggle"
          aria-label={isLiked ? t('like.unlike') : t('like.like')}
          aria-pressed={isLiked}
          disabled={isToggling}
          onClick={toggleLike}
        >
          {likeCount}
        </Button>
      </div>
      {isExpanded && (
        <div
          ref={bodyRef}
          id={bodyId}
          className={classnames(
            'image-assistant-lightbox__comments-body',
            hasScrollbar && 'image-assistant-lightbox__comments-body--has-scrollbar',
          )}
        >
          {canComment && (
            <Composer
              placeholder={t('commentsModal.placeholder')}
              username={pageConfig?.currentUserUsername || ''}
              avatarUrl={pageConfig?.currentUserAvatarUrl || null}
              isSubmitting={isSubmitting}
              onSubmit={addComment}
            />
          )}
          {(submitFailed || limitReached) && (
            <div className="comments-modal__error" role="alert">
              {t(limitReached ? 'commentsModal.limitReached' : 'commentsModal.submitError')}
            </div>
          )}
          {comments.length === 0 && <EmptyState isLoading={isLoading} loadFailed={loadFailed} />}
          {comments.length > 0 && (
            <ul className="comments-modal__list">
              {comments.map(comment => (
                <CommentThread
                  key={comment.id}
                  comment={comment}
                  currentUserId={currentUserId}
                  locale={locale}
                  canComment={canComment}
                  isSubmitting={isSubmitting}
                  creatorLabel={t('imageComments.creatorBadge')}
                  onReply={addReply}
                  onDelete={handleDelete}
                />
              ))}
            </ul>
          )}
          {comments.length > 0 && isLoading && hasMore && (
            <div className="comments-modal__state" aria-live="polite">
              <Spinner size="small" />
            </div>
          )}
          {comments.length > 0 && loadFailed && (
            <div className="comments-modal__error" role="alert">
              {t('commentsModal.loadError')}
            </div>
          )}
          {comments.length > 0 && hasMore && (
            <div className="comments-modal__sentinel" ref={sentinelRef} aria-hidden="true" />
          )}
        </div>
      )}
    </aside>
  )
}

CommentsPanel.propTypes = {
  slug: string.isRequired,
  title: string,
}

export default memo(CommentsPanel)
